// HUD (IMPLEMENTATION §2.6 "Chrome contents", UX §2 HUD; lead decision L12): a wood plank with brass rivets —
// coin + cash (24 px / 700, tabular digits; it tweens toward the sim value for ≤ hud.cashTweenMaxMs but
// SNAPS on any purchase, so the display never lies at buy time), the total income/s headline (14 px) and,
// from u18, the smaller UI-smoothed "auto $X/s" line (ui/hudRate.ts via ui/store.ts), plus the quick mute
// (48 hit). It re-renders only when the 10 Hz snapshot changes a shown number (≤ 25 renders in 2 s, M7
// bar): the cash tween writes the text node directly, never through a Preact render.
import { useLayoutEffect, useRef } from 'preact/hooks';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { money, perSec } from '../format.ts';
import { settingsSig, updateSettings } from '../settings.ts';
import { layoutSig, renderCounts } from '../state.ts';
import { hudSig } from '../store.ts';
import { box } from './box.ts';
import { Icon } from './Icon.tsx';

interface Tween {
  shown: number;
  from: number;
  to: number;
  t0: number;
  raf: number;
}

export function Hud() {
  renderCounts.hud += 1;
  const l = layoutSig.value;
  const h = hudSig.value;
  const muted = settingsSig.value.muted;
  const ctx = shellCtx();
  const f = ctx.shell.fmt();
  const hudCfg = ctx.data().ui.hud;
  const cashRef = useRef<HTMLSpanElement>(null);
  const tw = useRef<Tween>({ shown: h?.cash ?? 0, from: 0, to: 0, t0: 0, raf: 0 });
  const target = h?.cash ?? 0;

  // the cash text is owned by this effect (never a Preact child): a re-render cannot reset a running tween
  useLayoutEffect(() => {
    const t = tw.current;
    const el = cashRef.current;
    const write = (v: number): void => {
      t.shown = v;
      if (el) el.textContent = money(f, v);
    };
    cancelAnimationFrame(t.raf);
    // a purchase (cash went down) or the test build: snap
    if (ctx.instant || target <= t.shown) {
      write(target);
      return;
    }
    t.from = t.shown;
    t.to = target;
    t.t0 = performance.now();
    const dur = hudCfg.cashTweenMaxMs;
    const step = (now: number): void => {
      const k = Math.min(1, (now - t.t0) / dur);
      const e = 1 - (1 - k) * (1 - k); // Quad.out (juice easing.out)
      write(Math.floor(t.from + (t.to - t.from) * e));
      if (k < 1) t.raf = requestAnimationFrame(step);
    };
    t.raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(t.raf);
  }, [target, l !== null]);

  if (!l) return null;
  const toggleMute = (): void => {
    updateSettings({ muted: !muted });
    ctx.actions.settings(settingsSig.value);
  };
  const c = f.copy.hud;
  return (
    <div class="hud" style={box(l.hud)} data-testid="hud">
      <span class="hud-rivet l" />
      <span class="hud-rivet r" />
      <div class="hud-cash-wrap" data-fx="hudCash">
        <Icon frame="ui.coin" px={hudCfg.coinPx} class="hud-coin" />
        <span
          class="hud-cash"
          data-testid="hud-cash"
          ref={cashRef}
          style={{ fontSize: `${hudCfg.cashPx}px` }}
        />
      </div>
      <div class="hud-rates" data-fx="hudRate">
        <span class="hud-rate" data-testid="hud-rate" style={{ fontSize: `${hudCfg.ratePx}px` }}>
          {h ? perSec(f, h.total) : ''}
        </span>
        {h && h.auto !== null ? (
          <span
            class="hud-auto"
            data-testid="hud-auto"
            style={{ fontSize: `${hudCfg.autoRatePx}px` }}
          >
            {fillCopy(c.autoPerSec ?? 'auto {rate}/s', { rate: money(f, h.auto) })}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        class="hud-mute ctl"
        data-hit="mute"
        aria-label={muted ? (c.unmute ?? '') : (c.mute ?? '')}
        aria-pressed={muted}
        onClick={toggleMute}
      >
        <Icon frame={muted ? 'ui.speakerOff' : 'ui.speakerOn'} px={hudCfg.muteIconPx} />
      </button>
    </div>
  );
}
